import React, { useState, useEffect, useCallback, useRef } from 'react';
import { messagingAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  MessageSquare, Send, Loader2, Search, User, Clock, ChevronRight,
  FileText, Link as LinkIcon, Copy, Check, ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MessagingPage = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [messages, setMessages] = useState([]);
  const [bookingDetail, setBookingDetail] = useState(null);
  const [portalToken, setPortalToken] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [copied, setCopied] = useState(false);
  const messagesEndRef = useRef(null);

  const loadConversations = useCallback(async () => {
    try {
      const resp = await messagingAPI.conversations();
      setConversations(resp.data);
    } catch (err) {
      console.error('Load conversations error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  const loadConversation = useCallback(async (bookingId) => {
    try {
      const resp = await messagingAPI.conversation(bookingId);
      setMessages(resp.data.messages);
      setBookingDetail(resp.data.booking);
      setPortalToken(resp.data.portal_token);
      setSelectedBooking(bookingId);
    } catch (err) {
      console.error('Load conversation error:', err);
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedBooking) return;
    setSending(true);
    try {
      await messagingAPI.send({ booking_id: selectedBooking, content: newMessage });
      setNewMessage('');
      loadConversation(selectedBooking);
      loadConversations();
    } catch (err) {
      console.error('Send error:', err);
    } finally {
      setSending(false);
    }
  };

  const handleGeneratePortal = async () => {
    if (!selectedBooking) return;
    try {
      const resp = await messagingAPI.generatePortal(selectedBooking);
      setPortalToken(resp.data.portal_token);
    } catch (err) {
      console.error('Portal gen error:', err);
    }
  };

  const handleCopyPortalLink = () => {
    const link = `${BACKEND_URL}/guest/${portalToken}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadTemplates = async () => {
    try {
      const resp = await messagingAPI.templates();
      setTemplates(resp.data);
    } catch (err) {
      console.error('Templates error:', err);
    }
  };

  const handleSendTemplate = async (templateId) => {
    if (!selectedBooking) return;
    setSending(true);
    try {
      await messagingAPI.sendTemplate(selectedBooking, templateId);
      setTemplateDialogOpen(false);
      loadConversation(selectedBooking);
      loadConversations();
    } catch (err) {
      console.error('Template send error:', err);
    } finally {
      setSending(false);
    }
  };

  const filteredConversations = conversations.filter(c =>
    c.guest_name?.toLowerCase().includes(search.toLowerCase()) ||
    c.property_name?.toLowerCase().includes(search.toLowerCase())
  );

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-10 h-10 text-airbnb-pink animate-spin" /></div>;

  return (
    <div data-testid="messaging-page" className="animate-fade-in">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-airbnb-gray">Messaging</h1>
        <p className="text-airbnb-medium-gray mt-1">Communicate with your guests</p>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ height: 'calc(100vh - 200px)', minHeight: '500px' }}>
        <div className="flex h-full">
          {/* Conversation List */}
          <div className={`w-full md:w-80 lg:w-96 border-r border-gray-100 flex flex-col ${selectedBooking ? 'hidden md:flex' : 'flex'}`} data-testid="conversation-list">
            <div className="p-3 border-b border-gray-100">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-airbnb-medium-gray" />
                <Input
                  data-testid="message-search"
                  placeholder="Search guests..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-9 text-sm rounded-xl"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredConversations.length === 0 ? (
                <div className="text-center py-10 text-airbnb-medium-gray text-sm">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p>No conversations yet</p>
                  <p className="text-xs mt-1">Messages will appear when you have bookings</p>
                </div>
              ) : (
                filteredConversations.map((c) => (
                  <button
                    key={c.booking_id}
                    onClick={() => loadConversation(c.booking_id)}
                    data-testid={`conv-${c.booking_id}`}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                      selectedBooking === c.booking_id ? 'bg-airbnb-light-pink/30' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-airbnb-light-pink flex items-center justify-center text-airbnb-pink font-bold text-sm shrink-0">
                        {c.guest_name?.charAt(0)?.toUpperCase() || 'G'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-semibold text-airbnb-gray truncate">{c.guest_name}</span>
                          <span className="text-[10px] text-airbnb-medium-gray">{formatTime(c.last_message_at)}</span>
                        </div>
                        <p className="text-xs text-airbnb-medium-gray truncate">{c.property_name}</p>
                        <p className="text-xs text-airbnb-medium-gray truncate mt-0.5">{c.last_message || 'No messages yet'}</p>
                      </div>
                      {c.unread_count > 0 && (
                        <span className="bg-airbnb-pink text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center shrink-0">
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Message Area */}
          <div className={`flex-1 flex flex-col ${selectedBooking ? 'flex' : 'hidden md:flex'}`}>
            {!selectedBooking ? (
              <div className="flex-1 flex items-center justify-center text-airbnb-medium-gray" data-testid="no-conversation-selected">
                <div className="text-center">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p className="font-medium">Select a conversation</p>
                  <p className="text-sm mt-1">Choose a guest to start messaging</p>
                </div>
              </div>
            ) : (
              <>
                {/* Header */}
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between" data-testid="conversation-header">
                  <div className="flex items-center gap-3">
                    <button
                      className="md:hidden p-1"
                      onClick={() => setSelectedBooking(null)}
                    >
                      <ChevronRight className="w-5 h-5 rotate-180 text-airbnb-gray" />
                    </button>
                    <div>
                      <h3 className="text-sm font-bold text-airbnb-gray">{bookingDetail?.guest_name}</h3>
                      <p className="text-xs text-airbnb-medium-gray">
                        {bookingDetail?.property_name} &middot; {bookingDetail?.check_in} - {bookingDetail?.check_out}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {portalToken ? (
                      <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={handleCopyPortalLink} data-testid="copy-portal-link">
                        {copied ? <Check className="w-3.5 h-3.5 mr-1 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
                        Portal Link
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={handleGeneratePortal} data-testid="generate-portal-btn">
                        <LinkIcon className="w-3.5 h-3.5 mr-1" /> Guest Portal
                      </Button>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="messages-area">
                  {messages.length === 0 && (
                    <div className="text-center py-10 text-airbnb-medium-gray text-sm">
                      <p>No messages yet. Send a welcome message to your guest!</p>
                    </div>
                  )}
                  {messages.map((msg) => (
                    <div
                      key={msg.message_id}
                      className={`flex ${msg.sender_type === 'host' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`msg-${msg.message_id}`}
                    >
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                        msg.sender_type === 'host'
                          ? 'bg-airbnb-pink text-white rounded-br-md'
                          : 'bg-gray-100 text-airbnb-gray rounded-bl-md'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <p className={`text-[10px] mt-1 ${
                          msg.sender_type === 'host' ? 'text-white/70' : 'text-airbnb-medium-gray'
                        }`}>
                          {formatTime(msg.created_at)}
                          {msg.template_id && <span className="ml-1 opacity-70">&middot; Template</span>}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="px-4 py-3 border-t border-gray-100" data-testid="message-input-area">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost" size="sm"
                      className="h-10 shrink-0"
                      onClick={() => { loadTemplates(); setTemplateDialogOpen(true); }}
                      data-testid="template-btn"
                    >
                      <FileText className="w-4 h-4" />
                    </Button>
                    <Input
                      data-testid="message-input"
                      placeholder="Type a message..."
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                      className="flex-1 rounded-xl h-10"
                    />
                    <Button
                      data-testid="send-message-btn"
                      onClick={handleSend}
                      disabled={!newMessage.trim() || sending}
                      className="bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl h-10 px-4 shrink-0"
                    >
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Template Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="w-5 h-5 text-airbnb-pink" /> Message Templates</DialogTitle>
            <DialogDescription className="sr-only">Select a message template to send</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {templates.length === 0 ? (
              <p className="text-sm text-airbnb-medium-gray text-center py-4">Loading templates...</p>
            ) : (
              templates.filter(t => t.is_active).map((t) => (
                <button
                  key={t.template_id}
                  onClick={() => handleSendTemplate(t.template_id)}
                  disabled={sending}
                  data-testid={`tmpl-${t.template_id}`}
                  className="w-full text-left p-3 rounded-xl border border-gray-100 hover:border-airbnb-pink/30 hover:bg-airbnb-light-pink/10 transition-all"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-airbnb-gray">{t.name}</span>
                    {t.is_default && <span className="text-[10px] bg-gray-100 text-airbnb-medium-gray px-2 py-0.5 rounded-full">Default</span>}
                  </div>
                  <p className="text-xs text-airbnb-medium-gray mt-1">{t.subject}</p>
                  <p className="text-[10px] text-airbnb-medium-gray mt-0.5 capitalize">
                    Trigger: {t.trigger.replace('_', ' ')}
                    {t.trigger_days_before > 0 && ` (${t.trigger_days_before} day(s) before)`}
                  </p>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MessagingPage;
